import { ArrowRight, ShieldCheck } from 'lucide-react'

import { ButtonLink } from '@/components/ButtonLink'
import { InnerHero } from '@/components/InnerHero'
import { ResourceCard } from '@/components/ResourceCard'
import { Reveal } from '@/components/Reveal'
import { SectionIntro } from '@/components/SectionIntro'
import { resourceItems } from '@/data/site'

const questions = [
  '¿Qué objetivo persigue esta alternativa?',
  '¿Qué evidencia, riesgos y costos debemos conocer?',
  '¿Cómo se medirá el avance y cuándo se revisará el plan?',
  '¿Cómo se coordina con las demás terapias y especialistas?',
]

export function ResourcesPage() {
  return (
    <>
      <InnerHero
        eyebrow="Biblioteca viva"
        title="Recursos para orientarte"
        description="Un inventario en crecimiento para ayudarte a preparar preguntas, ordenar opciones y encontrar personas que puedan acompañarte."
      />

      <section className="section-space pt-0" aria-labelledby="resource-library-title">
        <div className="container-wide">
          <SectionIntro
            eyebrow="Explora por categoría"
            title="Empieza por lo que hoy necesitas."
            description="La biblioteca se está migrando y revisando para que cada recurso sea claro, útil y fácil de actualizar."
          />
          <div className="resource-grid" id="resource-library-title">
            {resourceItems.map((item, index) => (
              <ResourceCard item={item} index={index} key={item.title} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-space bg-ink-deep text-white" aria-labelledby="questions-title">
        <div className="container-wide story-grid">
          <Reveal>
            <p className="eyebrow !text-apricot before:!bg-apricot">Antes de decidir</p>
            <h2 className="section-title" id="questions-title">
              Cuatro preguntas que abren mejores conversaciones.
            </h2>
            <p className="lede mt-7 !text-white/65">
              Lleva tus notas a la consulta y pide explicaciones en un lenguaje que tu
              familia pueda comprender.
            </p>
          </Reveal>

          <div className="grid gap-3">
            {questions.map((question, index) => (
              <Reveal delay={index * 0.07} key={question}>
                <article className="flex items-center gap-4 rounded-[1.5rem] border border-white/15 bg-white/6 p-5 md:p-7">
                  <span className="grid size-11 shrink-0 place-items-center rounded-full bg-orange font-display text-lg">
                    {index + 1}
                  </span>
                  <p className="m-0 text-base leading-relaxed font-semibold md:text-lg">
                    {question}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-space" aria-labelledby="route-teaser-title">
        <div className="container-wide">
          <Reveal>
            <div className="glass-card grid gap-8 p-7 md:grid-cols-[auto_1fr_auto] md:items-center md:p-12">
              <span className="grid size-20 place-items-center rounded-full bg-lime text-ink-deep">
                <ShieldCheck aria-hidden="true" size={36} strokeWidth={1.5} />
              </span>
              <div>
                <p className="eyebrow">Primeros pasos</p>
                <h2 className="font-display text-4xl leading-none font-semibold tracking-[-0.045em] md:text-6xl" id="route-teaser-title">
                  ¿No sabes por dónde empezar?
                </h2>
                <p className="lede mt-4">
                  La ruta crítica organiza acciones y preguntas sin sustituir la
                  orientación de tu equipo médico.
                </p>
              </div>
              <a
                className="grid size-16 place-items-center rounded-full bg-ink-deep text-white transition-transform hover:-translate-y-1"
                href="/ruta-critica/"
                aria-label="Abrir la ruta crítica"
              >
                <ArrowRight aria-hidden="true" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-[var(--section-space)]">
        <div className="container-wide text-center">
          <Reveal>
            <p className="mx-auto mb-6 max-w-2xl text-sm leading-relaxed text-ink-deep/60">
              Este contenido es informativo y no sustituye diagnóstico, tratamiento ni
              consejo médico profesional. Consulta siempre al equipo que conoce el caso.
            </p>
            <ButtonLink href="/contacto/" variant="dark">
              Recomendar un recurso
            </ButtonLink>
          </Reveal>
        </div>
      </section>
    </>
  )
}
